import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, ShieldCheck } from "lucide-react";
import { Button, Card, Badge, Logo } from "../components/kit";
import api from "../lib/api";
import { LogoUploader } from "../components/LogoUploader";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const DELIVERY = [
  { id: "one_on_one", title: "One-on-one", desc: "Mostly individual coaching." },
  { id: "small_group", title: "Small group", desc: "Cohorts and small pods." },
  { id: "events", title: "Large-scale events", desc: "Workshops, summits, stages." },
  { id: "async", title: "Async content", desc: "Courses, communities, content." },
  { id: "hybrid", title: "Hybrid", desc: "A mix of the above." },
];
const WISH = [
  { id: "action", title: "Who's actually taking action" },
  { id: "risk", title: "Who's at risk of disengaging" },
  { id: "blocker", title: "What's blocking the group most" },
];

export default function CoachOnboarding() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [step, setStep] = useState(0);
  const [f, setF] = useState({ program_name: "", member_count: "", delivery_mode: "", hybrid_lean: "", wish_knew: "", logo_url: "", accent_color: "#106958", trust_ack: false });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/coach/profile").then((r) => { if (r.data.onboarded) navigate("/coach"); }).catch(() => {}); }, []); // eslint-disable-line

  const steps = ["program", "delivery", "wish", "branding", "trust"];
  const next = () => setStep((s) => Math.min(s + 1, steps.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const canNext = () => {
    const k = steps[step];
    if (k === "program") return f.program_name.trim().length > 0;
    if (k === "delivery") return f.delivery_mode && (f.delivery_mode !== "hybrid" || f.hybrid_lean);
    if (k === "wish") return !!f.wish_knew;
    if (k === "trust") return f.trust_ack;
    return true;
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/coach/onboarding", { ...f, member_count: parseInt(f.member_count || "0", 10) });
      await refreshUser();
      toast.success("You're all set", { description: "Welcome to your Catalyst dashboard." });
      navigate("/coach");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save. Please try again.");
    } finally { setSaving(false); }
  };

  const k = steps[step];
  return (
    <div className="buktu-root" data-brand="catalyst" data-testid="coach-onboarding" style={{ minHeight: "100vh", background: "var(--surface-page)" }}>
      <div className="buktu-wrap" style={{ maxWidth: 640, paddingTop: "var(--space-8)", paddingBottom: "var(--space-20)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", marginBottom: "var(--space-6)" }}>
          <Logo height={28} to="catalyst" />
          <Badge tone="accent" dot>Coach setup · {step + 1} of {steps.length}</Badge>
        </div>

        <Card padding="var(--space-8)">
          {k === "program" && (
            <Step title="Tell us about your program" sub="The basics for your cohort.">
              <L label="Program name"><input data-testid="co-program" className="buktu-input" value={f.program_name} onChange={(e) => set("program_name", e.target.value)} placeholder="e.g. Scale Collective" /></L>
              <L label="Active member count"><input data-testid="co-members" className="buktu-input" type="number" min="0" value={f.member_count} onChange={(e) => set("member_count", e.target.value)} placeholder="How many are in your program right now?" /></L>
            </Step>
          )}
          {k === "delivery" && (
            <Step title="How do you deliver coaching?" sub="This sets how your dashboard is laid out.">
              {DELIVERY.map((o) => (
                <Opt key={o.id} selected={f.delivery_mode === o.id} testid={`co-delivery-${o.id}`} onClick={() => set("delivery_mode", o.id)} title={o.title} desc={o.desc} />
              ))}
              {f.delivery_mode === "hybrid" && (
                <div style={{ marginTop: "var(--space-4)", paddingTop: "var(--space-4)", borderTop: "1px solid var(--border-hairline)" }}>
                  <p style={{ font: "var(--type-label)", color: "var(--text-strong)", marginBottom: "var(--space-3)" }}>In a typical week, do you spend more time with individuals or the group as a whole?</p>
                  <Opt selected={f.hybrid_lean === "individual"} testid="co-hybrid-individual" onClick={() => set("hybrid_lean", "individual")} title="More with individuals" />
                  <Opt selected={f.hybrid_lean === "group"} testid="co-hybrid-group" onClick={() => set("hybrid_lean", "group")} title="More with the group" />
                </div>
              )}
            </Step>
          )}
          {k === "wish" && (
            <Step title="What's the one thing you wish you knew today that you don't?" sub="We'll pin that insight on your dashboard.">
              {WISH.map((o) => <Opt key={o.id} selected={f.wish_knew === o.id} testid={`co-wish-${o.id}`} onClick={() => set("wish_knew", o.id)} title={o.title} />)}
            </Step>
          )}
          {k === "branding" && (
            <Step title="Make it yours" sub="Your white-label branding shows across your dashboard.">
              <L label="Brand accent color">
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <input data-testid="co-accent" type="color" value={f.accent_color} onChange={(e) => set("accent_color", e.target.value)} style={{ width: 52, height: 40, border: "1px solid var(--border-hairline)", borderRadius: 8, cursor: "pointer", background: "none" }} />
                  <span style={{ font: "var(--type-data)", color: "var(--text-muted)" }}>{f.accent_color}</span>
                </div>
              </L>
              <L label="Logo (optional)">
                <LogoUploader value={f.logo_url} accent={f.accent_color} onUploaded={(u) => set("logo_url", u)} />
              </L>
            </Step>
          )}
          {k === "trust" && (
            <Step title="How member privacy works" sub="Please read before continuing.">
              <div style={{ padding: "var(--space-5)", borderRadius: "var(--radius-md)", background: "var(--surface-brand-wash)", display: "flex", gap: 12 }}>
                <span style={{ color: "var(--buktu-green)", flexShrink: 0 }}><ShieldCheck size={22} /></span>
                <div style={{ font: "var(--type-body-sm)", color: "var(--text-body)", lineHeight: 1.6 }}>
                  Your members control what you can see. By default you see <b>aggregate, anonymized patterns</b> across your cohort — not individual numbers. An individual's detail only appears to you if <b>that specific member has explicitly turned sharing on</b>. We never show a person's raw financial or sentiment data without their own opt-in.
                </div>
              </div>
              <label data-testid="co-trust-label" style={{ display: "flex", alignItems: "flex-start", gap: 10, marginTop: "var(--space-5)", cursor: "pointer", font: "var(--type-body-sm)", color: "var(--text-strong)" }}>
                <input data-testid="co-trust" type="checkbox" checked={f.trust_ack} onChange={(e) => set("trust_ack", e.target.checked)} style={{ marginTop: 3, width: 18, height: 18 }} />
                I've read and understand how member privacy works.
              </label>
            </Step>
          )}

          <div style={{ display: "flex", gap: "var(--space-3)", marginTop: "var(--space-6)" }}>
            {step > 0 && <Button variant="secondary" data-testid="co-back" onClick={back} iconLeft={<ArrowLeft size={16} />}>Back</Button>}
            {step < steps.length - 1
              ? <Button data-testid="co-next" onClick={next} disabled={!canNext()} iconRight={<ArrowRight size={16} />}>Continue</Button>
              : <Button data-testid="co-finish" onClick={submit} disabled={saving || !canNext()} iconRight={<Check size={16} />}>{saving ? "Saving…" : "Finish setup"}</Button>}
          </div>
        </Card>
      </div>
    </div>
  );
}

function Step({ title, sub, children }) {
  return (
    <div>
      <h1 style={{ font: "var(--type-heading)", fontSize: 24, marginBottom: 6, color: "var(--text-strong)" }}>{title}</h1>
      {sub && <p style={{ font: "var(--type-body-sm)", color: "var(--text-muted)", marginBottom: "var(--space-5)" }}>{sub}</p>}
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>{children}</div>
    </div>
  );
}
function L({ label, children }) {
  return <label style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", font: "var(--type-label)", color: "var(--text-strong)" }}>{label}{children}</label>;
}
function Opt({ selected, onClick, title, desc, testid }) {
  return (
    <button type="button" data-testid={testid} data-selected={selected} onClick={onClick} className="buktu-option"
      style={{ width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: 12, padding: "var(--space-3) var(--space-4)" }}>
      <span style={{ width: 18, height: 18, borderRadius: 999, flexShrink: 0, border: "2px solid " + (selected ? "var(--buktu-green)" : "var(--border-strong)"), background: selected ? "var(--buktu-green)" : "transparent", display: "grid", placeItems: "center", color: "#fff" }}>{selected && <Check size={11} />}</span>
      <span style={{ display: "flex", flexDirection: "column" }}>
        <span style={{ font: "var(--type-label)", color: "var(--text-strong)" }}>{title}</span>
        {desc && <span style={{ font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-muted)" }}>{desc}</span>}
      </span>
    </button>
  );
}
